import React, { useState, useEffect } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { notesAPI, tableAPI } from '../services/api';
import './Sidebar.css';

const Sidebar = () => {
  const navigate = useNavigate();
  const { logout, user } = useAuth();
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showCreateNoteModal, setShowCreateNoteModal] = useState(false);
  const [tableName, setTableName] = useState('');
  const [noteName, setNoteName] = useState('');
  const [numColumns, setNumColumns] = useState(4);
  const [columnNames, setColumnNames] = useState(['', '', '', '']);
  const [draggedIndex, setDraggedIndex] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);
  const [notes, setNotes] = useState([]);
  const [showExportModal, setShowExportModal] = useState(false);
  const [exportType, setExportType] = useState('monthly'); // 'monthly', 'stockRate', or 'filtered'
  const [exportMonth, setExportMonth] = useState('');
  const [exportYear, setExportYear] = useState(new Date().getFullYear().toString());
  const [stockRate, setStockRate] = useState('');
  const [exporting, setExporting] = useState(false);
  
  // Export filter options
  const [exportFilters, setExportFilters] = useState({
    client: '',
    source: '',
    code: '',
    dateFrom: '',
    dateTo: '',
    sellingRate: '',
    buyingRate: '',
    currency: '',
    purpose: '',
    amountMin: '',
    amountMax: '',
    profitMin: '',
    profitMax: '',
    expenseMin: '',
    expenseMax: '',
    notes: '',
    name: ''
  });
  
  const defaultTables = [
    { id: 1, name: 'A1', path: '/table1' },
    { id: 2, name: 'Expense List', path: '/table2' },
    { id: 3, name: 'Stock Updates', path: '/table3' },
    { id: 6, name: 'Stock Balance', path: '/table6' },
    { id: 4, name: 'Transactions', path: '/table4' },
    { id: 5, name: 'Source and Client', path: '/table5' },
    { id: 7, name: 'Money Tracker', path: '/table7' },
  ];

  const [tables, setTables] = useState(() => {
    // Load table order from localStorage
    const savedOrder = localStorage.getItem('sidebarTableOrder');
    if (savedOrder) {
      try {
        const order = JSON.parse(savedOrder);
        // Reorder default tables based on saved order
        const orderedTables = order.map(id => 
          defaultTables.find(t => t.id === id)
        ).filter(Boolean);
        // Add any new tables that weren't in the saved order
        const orderedIds = new Set(order);
        const newTables = defaultTables.filter(t => !orderedIds.has(t.id));
        return [...orderedTables, ...newTables];
      } catch (e) {
        console.error('Error loading table order:', e);
        return defaultTables;
      }
    }
    return defaultTables;
  });

  // Function to load and sync tables from localStorage
  const loadTables = () => {
    // Load saved default table names
    const savedDefaultNames = localStorage.getItem('defaultTableNames');
    const defaultNamesMap = savedDefaultNames ? JSON.parse(savedDefaultNames) : {};
    
    // Apply saved names to default tables
    const tablesWithSavedNames = defaultTables.map(table => ({
      ...table,
      name: defaultNamesMap[table.id] || table.name
    }));
    
    // Start with default tables
    const savedOrder = localStorage.getItem('sidebarTableOrder');
    let orderedTables = tablesWithSavedNames;
    
    if (savedOrder) {
      try {
        const order = JSON.parse(savedOrder);
        orderedTables = order.map(id => 
          tablesWithSavedNames.find(t => t.id === id)
        ).filter(Boolean);
        const orderedIds = new Set(order);
        const newTables = tablesWithSavedNames.filter(t => !orderedIds.has(t.id));
        orderedTables = [...orderedTables, ...newTables];
      } catch (e) {
        console.error('Error loading table order:', e);
      }
    }

    // Load custom tables
    const savedTables = localStorage.getItem('customTables');
    if (savedTables) {
      try {
        const customTables = JSON.parse(savedTables);
        // Merge custom tables, updating names if they exist
        const existingIds = new Set(orderedTables.map(t => t.id || t.tableId));
        const updatedCustomTables = customTables.map(customTable => {
          const existing = orderedTables.find(t => (t.id || t.tableId) === customTable.id || (t.id || t.tableId) === customTable.tableId);
          if (existing) {
            // Update name if it exists in the list
            return { ...existing, name: customTable.name };
          }
          return customTable;
        });
        
        // Add new custom tables that don't exist yet
        const newCustomTables = customTables.filter(t => !existingIds.has(t.id) && !existingIds.has(t.tableId));
        
        // Update existing tables with new names and add new ones
        const updatedTables = orderedTables.map(table => {
          const customMatch = customTables.find(ct => (ct.id || ct.tableId) === (table.id || table.tableId));
          if (customMatch) {
            return { ...table, name: customMatch.name };
          }
          return table;
        });
        
        return [...updatedTables, ...newCustomTables];
      } catch (e) {
        console.error('Error loading custom tables:', e);
        return orderedTables;
      }
    }
    
    return orderedTables;
  };

  useEffect(() => {
    // Initial load
    setTables(loadTables());
    
    // Listen for storage changes (when customTables or defaultTableNames is updated)
    const handleStorageChange = (e) => {
      if (e.key === 'customTables' || e.key === 'defaultTableNames' || e.key === null) {
        setTables(loadTables());
      }
    };
    
    window.addEventListener('storage', handleStorageChange);
    
    // Also listen for custom events (for same-tab updates)
    const handleCustomStorageChange = () => {
      setTables(loadTables());
    };
    
    // Create a custom event listener for same-tab updates
    window.addEventListener('customTablesUpdated', handleCustomStorageChange);
    window.addEventListener('defaultTableNameUpdated', handleCustomStorageChange);
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('customTablesUpdated', handleCustomStorageChange);
      window.removeEventListener('defaultTableNameUpdated', handleCustomStorageChange);
    };
  }, []);

  // Save table order to localStorage whenever it changes
  useEffect(() => {
    const tableIds = tables.map(t => t.id);
    localStorage.setItem('sidebarTableOrder', JSON.stringify(tableIds));
  }, [tables]);

  const handleCreateTable = () => {
    setShowCreateModal(true);
  };

  const handleNumColumnsChange = (e) => {
    const num = parseInt(e.target.value) || 1;
    setNumColumns(num);
    const newColumns = Array(num).fill('').map((_, i) => columnNames[i] || '');
    setColumnNames(newColumns);
  };

  const handleColumnNameChange = (index, value) => {
    const newColumns = [...columnNames];
    newColumns[index] = value;
    setColumnNames(newColumns);
  };

  const handleCreateSubmit = () => {
    if (!tableName.trim()) {
      alert('Please enter a table name');
      return;
    }
    
    const validColumns = columnNames.filter(name => name.trim() !== '');
    if (validColumns.length === 0) {
      alert('Please enter at least one column name');
      return;
    }

    // Create table data structure
    const tableId = `table${Date.now()}`;
    const path = `/table/${tableId}`;
    
    // Prepare headers with 'S no' as first column
    const headers = ['S no', ...validColumns, 'Notes'];
    const initialData = [headers, Array(headers.length).fill('')];

    // Save table definition
    const customTables = JSON.parse(localStorage.getItem('customTables') || '[]');
    const newTable = {
      id: tableId,
      name: tableName.trim(),
      path: path,
      headers: headers,
      tableId: tableId
    };
    customTables.push(newTable);
    localStorage.setItem('customTables', JSON.stringify(customTables));

    // Save initial table data
    localStorage.setItem(`table_${tableId}`, JSON.stringify(initialData));

    // Update tables list
    setTables(prev => [...prev, newTable]);

    // Reset form
    setShowCreateModal(false);
    setTableName('');
    setNumColumns(4);
    setColumnNames(['', '', '', '']);

    // Navigate to new table
    navigate(path);
  };

  const handleCancel = () => {
    setShowCreateModal(false);
    setTableName('');
    setNumColumns(4);
    setColumnNames(['', '', '', '']);
  };

  const handleDragStart = (e, index) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/html', e.target);
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverIndex(index);
  };

  const handleDragLeave = () => {
    setDragOverIndex(null);
  };

  const handleDrop = (e, dropIndex) => {
    e.preventDefault();
    
    if (draggedIndex === null || draggedIndex === dropIndex) {
      setDraggedIndex(null);
      setDragOverIndex(null);
      return;
    }

    const newTables = [...tables];
    const draggedTable = newTables[draggedIndex];
    
    // Remove dragged item
    newTables.splice(draggedIndex, 1);
    
    // Insert at new position
    newTables.splice(dropIndex, 0, draggedTable);
    
    setTables(newTables);
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  const handleDeleteTable = (tableId, e) => {
    e.preventDefault();
    e.stopPropagation();
    
    // Confirm deletion
    if (!window.confirm('Are you sure you want to delete this table? This action cannot be undone.')) {
      return;
    }

    // Check if it's a custom table (not a default table)
    const isDefaultTable = defaultTables.some(t => t.id === tableId || t.id === parseInt(tableId));
    if (isDefaultTable) {
      alert('Cannot delete default tables');
      return;
    }

    // Remove from customTables in localStorage
    const customTables = JSON.parse(localStorage.getItem('customTables') || '[]');
    const updatedCustomTables = customTables.filter(t => t.id !== tableId && t.tableId !== tableId);
    localStorage.setItem('customTables', JSON.stringify(updatedCustomTables));

    // Remove table data from localStorage
    localStorage.removeItem(`table_${tableId}`);

    // Remove from tables list
    setTables(prev => prev.filter(t => t.id !== tableId && t.tableId !== tableId));

    // Navigate to dashboard if we're currently viewing this table
    if (window.location.pathname === `/table/${tableId}`) {
      navigate('/dashboard');
    }
  };

  // Check if a table is custom (not default)
  const isCustomTable = (table) => {
    return !defaultTables.some(t => t.id === table.id || t.id === parseInt(table.id));
  };

  // Load notes
  useEffect(() => {
    const loadNotes = async () => {
      try {
        const loadedNotes = await notesAPI.getAll();
        setNotes(loadedNotes || []);
      } catch (error) {
        console.error('Error loading notes:', error);
        setNotes([]);
      }
    };
    loadNotes();
    
    // Refresh notes every 5 seconds to catch new notes
    const interval = setInterval(loadNotes, 5000);
    return () => clearInterval(interval);
  }, []);

  // Handle create note
  const handleCreateNote = async () => {
    if (!noteName.trim()) {
      alert('Please enter a note name');
      return;
    }

    try {
      const result = await notesAPI.create(noteName.trim());
      setNoteName('');
      setShowCreateNoteModal(false);
      // Reload notes
      const loadedNotes = await notesAPI.getAll();
      setNotes(loadedNotes || []);
      // Navigate to new note
      navigate(`/note/${result.noteId}`);
    } catch (error) {
      console.error('Error creating note:', error);
      alert('Failed to create note. Please try again.');
    }
  };

  const handleCancelNote = () => {
    setShowCreateNoteModal(false);
    setNoteName('');
  };

  const handleDeleteNote = async (noteId, e) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (!window.confirm('Are you sure you want to delete this note? This action cannot be undone.')) {
      return;
    }

    try {
      await notesAPI.delete(noteId);
      // Reload notes
      const loadedNotes = await notesAPI.getAll();
      setNotes(loadedNotes || []);
      
      // Navigate to dashboard if we're currently viewing this note
      if (window.location.pathname === `/note/${noteId}`) {
        navigate('/dashboard');
      }
    } catch (error) {
      console.error('Error deleting note:', error);
      alert('Failed to delete note. Please try again.');
    }
  };

  // Track stock rate history across all tables
  const getStockRateHistory = (targetRate) => {
    const history = [];
    const rate = parseFloat(targetRate);
    if (isNaN(rate)) return history;

    // Parse date helper
    const parseDate = (dateStr) => {
      if (!dateStr) return null;
      const parts = String(dateStr).trim().split('/');
      if (parts.length !== 3) return null;
      const day = parseInt(parts[0], 10);
      const month = parseInt(parts[1], 10);
      const year = parseInt(parts[2], 10);
      if (isNaN(day) || isNaN(month) || isNaN(year)) return null;
      return new Date(year, month - 1, day);
    };

    // Load Table3 (Stock Updates)
    const table3Data = localStorage.getItem('table_table3');
    if (table3Data) {
      try {
        const parsed = JSON.parse(table3Data);
        if (parsed && parsed.length > 1) {
          const headers = parsed[0];
          const nameIndex = headers.indexOf('Name');
          const dateIndex = headers.indexOf('Date');
          const sourceIndex = headers.indexOf('Source');
          const buyingRateIndex = headers.indexOf('Buying Rate');
          const amountIndex = headers.indexOf('Amount');
          const currencyIndex = headers.indexOf('Currency');
          const notesIndex = headers.indexOf('Notes');

          parsed.slice(1).forEach((row, idx) => {
            if (!row || row.length === 0) return;
            const rowRate = parseFloat(row[buyingRateIndex] || 0);
            // Match by buying rate only - include all currencies (currency is shown in the table)
            if (Math.abs(rowRate - rate) < 0.01) {
              history.push({
                type: 'Stock Update',
                date: row[dateIndex] || '',
                name: row[nameIndex] || '',
                source: row[sourceIndex] || '',
                buyingRate: rowRate,
                amount: row[amountIndex] || '',
                currency: row[currencyIndex] || '',
                notes: row[notesIndex] || '',
                parsedDate: parseDate(row[dateIndex])
              });
            }
          });
        }
      } catch (e) {
        console.error('Error parsing table3:', e);
      }
    }

    // Load Table4 (Transactions)
    const table4Data = localStorage.getItem('table_table4');
    if (table4Data) {
      try {
        const parsed = JSON.parse(table4Data);
        if (parsed && parsed.length > 1) {
          const headers = parsed[0];
          const dateIndex = headers.indexOf('Date');
          const nameIndex = headers.indexOf('Name');
          const codeIndex = headers.indexOf('Code');
          const aedIndex = headers.indexOf('AED');
          const sellingRateIndex = headers.indexOf('Selling Rate');
          const inrIndex = headers.indexOf('INR');
          const buyingRateIndex = headers.indexOf('Buying Rate');
          const stockInrIndex = headers.indexOf('Stock INR');
          const crossProfitIndex = headers.indexOf('Cross Profit');
          const expenseIndex = headers.indexOf('Expense');
          const netProfitIndex = headers.indexOf('Net Profit');
          const notesIndex = headers.indexOf('Notes');

          parsed.slice(1).forEach((row, idx) => {
            if (!row || row.length === 0) return;
            const rowRate = parseFloat(row[buyingRateIndex] || 0);
            // Only match INR records (buying rate is for INR currency)
            if (Math.abs(rowRate - rate) < 0.01) {
              history.push({
                type: 'Transaction',
                date: row[dateIndex] || '',
                name: row[nameIndex] || '',
                code: row[codeIndex] || '',
                aed: row[aedIndex] || '',
                sellingRate: row[sellingRateIndex] || '',
                inr: row[inrIndex] || '',
                buyingRate: rowRate,
                stockInr: row[stockInrIndex] || '',
                crossProfit: row[crossProfitIndex] || '',
                expense: row[expenseIndex] || '',
                netProfit: row[netProfitIndex] || '',
                notes: row[notesIndex] || '',
                parsedDate: parseDate(row[dateIndex])
              });
            }
          });
        }
      } catch (e) {
        console.error('Error parsing table4:', e);
      }
    }

    // Load Table6 (Stock Balance) - current balance
    const table6Data = localStorage.getItem('table_table6');
    if (table6Data) {
      try {
        const parsed = JSON.parse(table6Data);
        if (parsed && parsed.length > 1) {
          const headers = parsed[0];
          const buyingRateIndex = headers.indexOf('Buying Rate');
          const totalAmountIndex = headers.indexOf('Total Amount');
          const currencyIndex = headers.indexOf('Currency');
          const notesIndex = headers.indexOf('Notes');

          parsed.slice(1).forEach((row, idx) => {
            if (!row || row.length === 0) return;
            const rowRate = parseFloat(row[buyingRateIndex] || 0);
            const currency = String(row[currencyIndex] || '').trim().toUpperCase();
            // Only match INR records (buying rate is for INR currency)
            if (Math.abs(rowRate - rate) < 0.01 && currency === 'INR') {
              history.push({
                type: 'Current Balance',
                date: 'Current',
                buyingRate: rowRate,
                totalAmount: row[totalAmountIndex] || '',
                currency: row[currencyIndex] || '',
                notes: row[notesIndex] || '',
                parsedDate: new Date() // Current balance is always latest
              });
            }
          });
        }
      } catch (e) {
        console.error('Error parsing table6:', e);
      }
    }

    // Sort by date (most recent first, but Current Balance at top)
    history.sort((a, b) => {
      if (a.type === 'Current Balance') return -1;
      if (b.type === 'Current Balance') return 1;
      if (!a.parsedDate && !b.parsedDate) return 0;
      if (!a.parsedDate) return 1;
      if (!b.parsedDate) return -1;
      return b.parsedDate - a.parsedDate;
    });

    return history;
  };

  // Export stock rate history to PDF
  const exportStockRateHistory = async () => {
    if (!stockRate || isNaN(parseFloat(stockRate))) {
      alert('Please enter a valid stock rate');
      return;
    }

    try {
      setExporting(true);
      const history = getStockRateHistory(stockRate);

      if (history.length === 0) {
        alert(`No history found for stock rate ${stockRate}`);
        setExporting(false);
        return;
      }

      // Import jsPDF dynamically
      const { jsPDF } = await import('jspdf');
      const autoTable = (await import('jspdf-autotable')).default;

      const doc = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4'
      });

      // Title
      doc.setFontSize(18);
      doc.setFont(undefined, 'bold');
      doc.text(`Stock Rate History: ${stockRate}`, 14, 20);

      // Export date
      doc.setFontSize(10);
      doc.setFont(undefined, 'normal');
      const exportDate = new Date().toLocaleDateString('en-US', { 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
      doc.text(`Exported on: ${exportDate}`, 14, 28);
      doc.text(`Total Records: ${history.length}`, 14, 34);

      // Separate data by type
      const stockUpdates = history.filter(item => item.type === 'Stock Update');
      const transactions = history.filter(item => item.type === 'Transaction');
      const currentBalance = history.filter(item => item.type === 'Current Balance');

      let currentY = 42;

      // Helper function to add table
      const addTable = (title, headers, data, startY) => {
        if (data.length === 0) return startY;

        // Add section title
        doc.setFontSize(12);
        doc.setFont(undefined, 'bold');
        doc.text(title, 14, startY);
        
        const tableStartY = startY + 6;

        // Prepare table data
        const tableData = data.map(item => {
          if (item.type === 'Stock Update') {
            return [
              item.date || '',
              item.name || '',
              item.source || '',
              item.buyingRate?.toFixed(2) || '',
              item.amount || '',
              item.currency || '',
              item.notes || ''
            ];
          } else if (item.type === 'Transaction') {
            return [
              item.date || '',
              item.name || '',
              item.code || '',
              item.aed || '',
              item.sellingRate || '',
              item.buyingRate?.toFixed(2) || '',
              item.stockInr || '',
              item.crossProfit || '',
              item.expense || '',
              item.netProfit || '',
              item.notes || ''
            ];
          } else {
            return [
              item.date || '',
              '-',
              '-',
              item.buyingRate?.toFixed(2) || '',
              item.totalAmount || '',
              item.currency || '',
              item.notes || ''
            ];
          }
        });

        autoTable(doc, {
          head: [headers],
          body: tableData,
          startY: tableStartY,
          styles: {
            fontSize: 7,
            cellPadding: 1.5,
            overflow: 'linebreak',
            cellWidth: 'wrap'
          },
          headStyles: {
            fillColor: [249, 250, 251],
            textColor: [17, 24, 39],
            fontStyle: 'bold',
            halign: 'left'
          },
          alternateRowStyles: {
            fillColor: [255, 255, 255]
          },
          margin: { top: tableStartY, left: 14, right: 14 },
          didDrawPage: function (data) {
            doc.setFontSize(8);
            doc.text(
              `Page ${data.pageNumber}`,
              doc.internal.pageSize.getWidth() / 2,
              doc.internal.pageSize.getHeight() - 10,
              { align: 'center' }
            );
          }
        });

        // Return the Y position after the table
        return doc.lastAutoTable.finalY + 10;
      };

      // Add Current Balance table first
      if (currentBalance.length > 0) {
        currentY = addTable(
          'Current Balance',
          ['Date', 'Name', 'Code', 'Buying Rate', 'Total Amount', 'Currency', 'Notes'],
          currentBalance,
          currentY
        );
      }

      // Add Stock Updates table
      if (stockUpdates.length > 0) {
        currentY = addTable(
          'Stock Updates',
          ['Date', 'Name', 'Source', 'Buying Rate', 'Amount', 'Currency', 'Notes'],
          stockUpdates,
          currentY
        );
      }

      // Add Transactions table
      if (transactions.length > 0) {
        currentY = addTable(
          'Transactions',
          ['Date', 'Name', 'Code', 'AED', 'Selling Rate', 'Buying Rate', 'Stock INR', 'Cross Profit', 'Expense', 'Net Profit', 'Notes'],
          transactions,
          currentY
        );
      }

      // Save PDF
      doc.save(`Stock_Rate_History_${stockRate}_${new Date().toISOString().split('T')[0]}.pdf`);

      setShowExportModal(false);
      setStockRate('');
      alert('Export completed successfully!');
    } catch (error) {
      console.error('Error exporting stock rate history:', error);
      alert(`Failed to export: ${error.message}`);
    } finally {
      setExporting(false);
    }
  };

  const handleExport = async () => {
    if (exportType === 'stockRate') {
      await exportStockRateHistory();
      return;
    }

    if (exportType === 'filtered') {
      await exportFilteredData();
      return;
    }

    if (!exportMonth || !exportYear) {
      alert('Please select both month and year');
      return;
    }

    try {
      setExporting(true);
      const month = parseInt(exportMonth);
      const year = parseInt(exportYear);
      
      // Call export API
      const token = localStorage.getItem('token');
      const API_BASE_URL = process.env.REACT_APP_API_URL || 
        (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1' 
          ? 'http://localhost:5001/api' 
          : '/api');
      
      const response = await fetch(`${API_BASE_URL}/tables/export?month=${month}&year=${year}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      // Check content type first
      const contentType = response.headers.get('content-type');
      
      if (!response.ok) {
        if (contentType && contentType.includes('application/json')) {
          const errorData = await response.json();
          throw new Error(errorData.message || 'Export failed');
        } else {
          const text = await response.text().catch(() => 'Unknown error');
          throw new Error(`Export failed: ${response.status} ${response.statusText}. ${text.substring(0, 200)}`);
        }
      }

      // Check if response is PDF
      if (!contentType || !contentType.includes('application/pdf')) {
        const text = await response.text().catch(() => 'Could not read response');
        console.error('❌ [EXPORT] Server returned non-PDF response:', {
          contentType,
          status: response.status,
          statusText: response.statusText,
          body: text.substring(0, 500)
        });
        throw new Error(`Server did not return a PDF file. Content-Type: ${contentType || 'unknown'}. Response: ${text.substring(0, 200)}`);
      }

      // Download PDF
      const blob = await response.blob();
      
      // Verify blob is not empty
      if (blob.size === 0) {
        throw new Error('Received empty PDF file');
      }
      
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 
                         'July', 'August', 'September', 'October', 'November', 'December'];
      link.download = `Tables_Export_${monthNames[month - 1]}_${year}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);

      setShowExportModal(false);
      setExportMonth('');
      alert('Export completed successfully!');
    } catch (error) {
      console.error('Error exporting tables:', error);
      alert(`Failed to export: ${error.message}`);
    } finally {
      setExporting(false);
    }
  };

  // Get unique values from tables for filter dropdowns
  const getUniqueValues = (tableId, columnName) => {
    try {
      const tableData = localStorage.getItem(`table_${tableId}`);
      if (!tableData) return [];
      const parsed = JSON.parse(tableData);
      if (!parsed || parsed.length < 2) return [];
      
      const headers = parsed[0];
      const colIndex = headers.findIndex(h => h && h.toLowerCase() === columnName.toLowerCase());
      if (colIndex === -1) return [];
      
      const values = new Set();
      parsed.slice(1).forEach(row => {
        const value = String(row[colIndex] || '').trim();
        if (value) values.add(value);
      });
      return Array.from(values).sort();
    } catch (e) {
      return [];
    }
  };

  // Apply filters to all tables and export
  const exportFilteredData = async () => {
    try {
      setExporting(true);
      
      // Helper function to parse date
      const parseDate = (dateStr) => {
        if (!dateStr) return null;
        const parts = String(dateStr).trim().split('/');
        if (parts.length !== 3) return null;
        const day = parseInt(parts[0], 10);
        const month = parseInt(parts[1], 10);
        const year = parseInt(parts[2], 10);
        if (isNaN(day) || isNaN(month) || isNaN(year)) return null;
        return new Date(year, month - 1, day);
      };

      // Helper function to filter a table row
      const matchesFilters = (row, headers) => {
        if (!row || !Array.isArray(row) || row.length === 0) return false;

        // Filter by Client/Name - check both Name and Code columns
        if (exportFilters.client || exportFilters.name) {
          const filterValue = (exportFilters.client || exportFilters.name).trim().toLowerCase();
          let matches = false;
          
          // Check Name column
          const nameIndex = headers.findIndex(h => h && h.toLowerCase() === 'name');
          if (nameIndex !== -1) {
            const name = String(row[nameIndex] || '').trim().toLowerCase();
            if (name.includes(filterValue)) matches = true;
          }
          
          // Check Code column (for client codes like clt03)
          const codeIndex = headers.findIndex(h => h && h.toLowerCase() === 'code');
          if (codeIndex !== -1 && !matches) {
            const code = String(row[codeIndex] || '').trim().toLowerCase();
            if (code.includes(filterValue)) matches = true;
          }
          
          if (!matches) return false;
        }

        // Filter by Source
        if (exportFilters.source) {
          const sourceIndex = headers.findIndex(h => h && h.toLowerCase() === 'source');
          if (sourceIndex !== -1) {
            const source = String(row[sourceIndex] || '').trim().toLowerCase();
            if (!source.includes(exportFilters.source.toLowerCase())) return false;
          }
        }

        // Filter by Code
        if (exportFilters.code) {
          const codeIndex = headers.findIndex(h => h && h.toLowerCase() === 'code');
          if (codeIndex !== -1) {
            const code = String(row[codeIndex] || '').trim().toLowerCase();
            if (!code.includes(exportFilters.code.toLowerCase())) return false;
          }
        }

        // Filter by Date Range
        if (exportFilters.dateFrom || exportFilters.dateTo) {
          const dateIndex = headers.findIndex(h => h && h.toLowerCase() === 'date');
          if (dateIndex !== -1) {
            const dateStr = String(row[dateIndex] || '').trim();
            const rowDate = parseDate(dateStr);
            if (!rowDate) return false;
            
            const fromDate = exportFilters.dateFrom ? parseDate(exportFilters.dateFrom) : null;
            const toDate = exportFilters.dateTo ? parseDate(exportFilters.dateTo) : null;
            
            if (fromDate && rowDate < fromDate) return false;
            if (toDate && rowDate > toDate) return false;
          }
        }

        // Filter by Selling Rate
        if (exportFilters.sellingRate) {
          const sellingRateIndex = headers.findIndex(h => h && h.toLowerCase() === 'selling rate');
          if (sellingRateIndex !== -1) {
            const targetRate = parseFloat(exportFilters.sellingRate);
            if (!isNaN(targetRate)) {
              const rowRate = parseFloat(row[sellingRateIndex] || 0);
              if (Math.abs(rowRate - targetRate) >= 0.01) return false;
            }
          }
        }

        // Filter by Buying Rate
        if (exportFilters.buyingRate) {
          const buyingRateIndex = headers.findIndex(h => h && h.toLowerCase().includes('buying rate'));
          if (buyingRateIndex !== -1) {
            const targetRate = parseFloat(exportFilters.buyingRate);
            if (!isNaN(targetRate)) {
              const rowRate = parseFloat(row[buyingRateIndex] || 0);
              if (Math.abs(rowRate - targetRate) >= 0.01) return false;
            }
          }
        }

        // Filter by Currency
        if (exportFilters.currency && exportFilters.currency !== 'All Currencies') {
          const currencyIndex = headers.findIndex(h => h && h.toLowerCase() === 'currency');
          if (currencyIndex !== -1) {
            const currency = String(row[currencyIndex] || '').trim().toUpperCase();
            if (currency !== exportFilters.currency.toUpperCase()) return false;
          }
        }

        // Filter by Purpose
        if (exportFilters.purpose) {
          const purposeIndex = headers.findIndex(h => h && h.toLowerCase() === 'purpose');
          if (purposeIndex !== -1) {
            const purpose = String(row[purposeIndex] || '').trim().toLowerCase();
            if (!purpose.includes(exportFilters.purpose.toLowerCase())) return false;
          }
        }

        // Filter by Amount Range
        if (exportFilters.amountMin || exportFilters.amountMax) {
          const amountIndex = headers.findIndex(h => h && h.toLowerCase() === 'amount');
          if (amountIndex !== -1) {
            const amount = parseFloat(String(row[amountIndex] || '').replace(/[^0-9.-]/g, '')) || 0;
            const minAmount = exportFilters.amountMin ? parseFloat(exportFilters.amountMin) : -Infinity;
            const maxAmount = exportFilters.amountMax ? parseFloat(exportFilters.amountMax) : Infinity;
            if (amount < minAmount || amount > maxAmount) return false;
          }
        }

        // Filter by Profit Range
        if (exportFilters.profitMin || exportFilters.profitMax) {
          const profitIndex = headers.findIndex(h => h && (h.toLowerCase() === 'net profit' || h.toLowerCase() === 'net-profit'));
          if (profitIndex !== -1) {
            const profit = parseFloat(String(row[profitIndex] || '').replace(/[^0-9.-]/g, '')) || 0;
            const minProfit = exportFilters.profitMin ? parseFloat(exportFilters.profitMin) : -Infinity;
            const maxProfit = exportFilters.profitMax ? parseFloat(exportFilters.profitMax) : Infinity;
            if (profit < minProfit || profit > maxProfit) return false;
          }
        }

        // Filter by Expense Range
        if (exportFilters.expenseMin || exportFilters.expenseMax) {
          const expenseIndex = headers.findIndex(h => h && h.toLowerCase() === 'expense');
          if (expenseIndex !== -1) {
            const expense = parseFloat(String(row[expenseIndex] || '').replace(/[^0-9.-]/g, '')) || 0;
            const minExpense = exportFilters.expenseMin ? parseFloat(exportFilters.expenseMin) : -Infinity;
            const maxExpense = exportFilters.expenseMax ? parseFloat(exportFilters.expenseMax) : Infinity;
            if (expense < minExpense || expense > maxExpense) return false;
          }
        }

        // Filter by Notes
        if (exportFilters.notes) {
          const notesIndex = headers.findIndex(h => h && h.toLowerCase() === 'notes');
          if (notesIndex !== -1) {
            const notes = String(row[notesIndex] || '').trim().toLowerCase();
            if (!notes.includes(exportFilters.notes.toLowerCase())) return false;
          }
        }

        return true;
      };

      // Collect filtered data from all tables (same pattern as stock rate history)
      const filteredData = {
        'A1': { headers: [], rows: [] },
        'Expense List': { headers: [], rows: [] },
        'Stock Updates': { headers: [], rows: [] },
        'Transactions': { headers: [], rows: [] },
        'Source and Client': { headers: [], rows: [] },
        'Money Tracker - Money In': { headers: [], rows: [] },
        'Money Tracker - Money Out': { headers: [], rows: [] }
      };

      const tablesToProcess = [
        { id: 'table1', name: 'A1' },
        { id: 'table2', name: 'Expense List' },
        { id: 'table3', name: 'Stock Updates' },
        { id: 'table4', name: 'Transactions' },
        { id: 'table5', name: 'Source and Client' },
        { id: 'table7_moneyin', name: 'Money Tracker - Money In' },
        { id: 'table7_moneyout', name: 'Money Tracker - Money Out' }
      ];

      let totalRecords = 0;

      for (const table of tablesToProcess) {
        const tableData = localStorage.getItem(`table_${table.id}`);
        if (!tableData) continue;

        try {
          const parsed = JSON.parse(tableData);
          if (!parsed || parsed.length < 2) continue;

          const headers = parsed[0];
          let filteredRows;
          
          // Special handling for Money Tracker tables - only include if client/source filter is applied
          if (table.id === 'table7_moneyin' || table.id === 'table7_moneyout') {
            // Only include Money Tracker if there's a client/name or source filter
            if (!exportFilters.client && !exportFilters.name && !exportFilters.source) {
              continue; // Skip Money Tracker if no client/source filter
            }
            
            // Filter Money Tracker rows by Name or Code matching the filter
            filteredRows = parsed.slice(1).filter(row => {
              if (!row || !Array.isArray(row) || row.length === 0) return false;
              
              const nameIndex = headers.findIndex(h => h && h.toLowerCase() === 'name');
              const codeIndex = headers.findIndex(h => h && h.toLowerCase() === 'code');
              
              let matches = false;
              
              // Check if Name or Code matches client/name filter
              if (exportFilters.client || exportFilters.name) {
                const filterValue = (exportFilters.client || exportFilters.name).trim().toLowerCase();
                
                if (nameIndex !== -1) {
                  const name = String(row[nameIndex] || '').trim().toLowerCase();
                  if (name.includes(filterValue)) matches = true;
                }
                
                if (codeIndex !== -1 && !matches) {
                  const code = String(row[codeIndex] || '').trim().toLowerCase();
                  if (code.includes(filterValue)) matches = true;
                }
              }
              
              // For source filter, we need to check if the Name matches (sources are in table5)
              // Since Money Tracker uses Name/Code from table5, we check Name
              if (exportFilters.source && !matches) {
                const sourceFilter = exportFilters.source.trim().toLowerCase();
                if (nameIndex !== -1) {
                  const name = String(row[nameIndex] || '').trim().toLowerCase();
                  if (name.includes(sourceFilter)) matches = true;
                }
              }
              
              return matches;
            });
          } else {
            // For other tables, use the standard filter function
            filteredRows = parsed.slice(1).filter(row => matchesFilters(row, headers));
          }

          if (filteredRows.length > 0) {
            filteredData[table.name] = {
              headers: headers,
              rows: filteredRows
            };
            totalRecords += filteredRows.length;
          }
        } catch (e) {
          console.error(`Error processing ${table.name}:`, e);
        }
      }

      if (totalRecords === 0) {
        alert('No data found matching the selected filters');
        setExporting(false);
        return;
      }

      // Import jsPDF dynamically
      const { jsPDF } = await import('jspdf');
      const autoTable = (await import('jspdf-autotable')).default;

      const doc = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4'
      });

      // Title
      doc.setFontSize(18);
      doc.setFont(undefined, 'bold');
      doc.text('Filtered Data Export', 14, 20);

      // Export date
      doc.setFontSize(10);
      doc.setFont(undefined, 'normal');
      const exportDate = new Date().toLocaleDateString('en-US', { 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
      doc.text(`Exported on: ${exportDate}`, 14, 28);
      doc.text(`Total Records: ${totalRecords}`, 14, 34);

      // Filter info
      const activeFilters = [];
      if (exportFilters.client || exportFilters.name) activeFilters.push(`Client: ${exportFilters.client || exportFilters.name}`);
      if (exportFilters.source) activeFilters.push(`Source: ${exportFilters.source}`);
      if (exportFilters.code) activeFilters.push(`Code: ${exportFilters.code}`);
      if (exportFilters.dateFrom || exportFilters.dateTo) {
        activeFilters.push(`Date: ${exportFilters.dateFrom || '...'} to ${exportFilters.dateTo || '...'}`);
      }
      if (exportFilters.sellingRate) activeFilters.push(`Selling Rate: ${exportFilters.sellingRate}`);
      if (exportFilters.buyingRate) activeFilters.push(`Buying Rate: ${exportFilters.buyingRate}`);
      if (exportFilters.currency && exportFilters.currency !== 'All Currencies') activeFilters.push(`Currency: ${exportFilters.currency}`);
      if (exportFilters.purpose) activeFilters.push(`Purpose: ${exportFilters.purpose}`);
      if (exportFilters.amountMin || exportFilters.amountMax) {
        activeFilters.push(`Amount: ${exportFilters.amountMin || '...'} to ${exportFilters.amountMax || '...'}`);
      }
      if (exportFilters.profitMin || exportFilters.profitMax) {
        activeFilters.push(`Profit: ${exportFilters.profitMin || '...'} to ${exportFilters.profitMax || '...'}`);
      }
      if (exportFilters.expenseMin || exportFilters.expenseMax) {
        activeFilters.push(`Expense: ${exportFilters.expenseMin || '...'} to ${exportFilters.expenseMax || '...'}`);
      }
      if (exportFilters.notes) activeFilters.push(`Notes: ${exportFilters.notes}`);

      if (activeFilters.length > 0) {
        doc.setFontSize(9);
        doc.text(`Applied Filters: ${activeFilters.join(', ')}`, 14, 40);
      }

      let currentY = activeFilters.length > 0 ? 46 : 42;

      // Helper function to add table (same as stock rate history)
      const addTable = (title, headers, data, startY) => {
        if (!data || data.length === 0) return startY;

        // Add section title
        doc.setFontSize(12);
        doc.setFont(undefined, 'bold');
        doc.text(title, 14, startY);
        
        const tableStartY = startY + 6;

        // Prepare table data
        const tableDataRows = data.map(row => {
          if (!Array.isArray(row)) return [];
          return row.map(cell => String(cell || ''));
        });

        autoTable(doc, {
          head: [headers.map(h => String(h || ''))],
          body: tableDataRows,
          startY: tableStartY,
          styles: {
            fontSize: 7,
            cellPadding: 1.5,
            overflow: 'linebreak',
            cellWidth: 'wrap'
          },
          headStyles: {
            fillColor: [249, 250, 251],
            textColor: [17, 24, 39],
            fontStyle: 'bold',
            halign: 'left'
          },
          alternateRowStyles: {
            fillColor: [255, 255, 255]
          },
          margin: { top: tableStartY, left: 14, right: 14 },
          didDrawPage: function (data) {
            doc.setFontSize(8);
            doc.text(
              `Page ${data.pageNumber}`,
              doc.internal.pageSize.getWidth() / 2,
              doc.internal.pageSize.getHeight() - 10,
              { align: 'center' }
            );
          }
        });

        // Return the Y position after the table
        return doc.lastAutoTable.finalY + 10;
      };

      // Add tables in order (only if they have data)
      if (filteredData['A1'].rows.length > 0) {
        currentY = addTable('A1', filteredData['A1'].headers, filteredData['A1'].rows, currentY);
      }

      if (filteredData['Expense List'].rows.length > 0) {
        currentY = addTable('Expense List', filteredData['Expense List'].headers, filteredData['Expense List'].rows, currentY);
      }

      if (filteredData['Stock Updates'].rows.length > 0) {
        currentY = addTable('Stock Updates', filteredData['Stock Updates'].headers, filteredData['Stock Updates'].rows, currentY);
      }

      if (filteredData['Transactions'].rows.length > 0) {
        currentY = addTable('Transactions', filteredData['Transactions'].headers, filteredData['Transactions'].rows, currentY);
      }

      if (filteredData['Source and Client'].rows.length > 0) {
        currentY = addTable('Source and Client', filteredData['Source and Client'].headers, filteredData['Source and Client'].rows, currentY);
      }

      if (filteredData['Money Tracker - Money In'].rows.length > 0) {
        currentY = addTable('Money Tracker - Money In', filteredData['Money Tracker - Money In'].headers, filteredData['Money Tracker - Money In'].rows, currentY);
      }

      if (filteredData['Money Tracker - Money Out'].rows.length > 0) {
        currentY = addTable('Money Tracker - Money Out', filteredData['Money Tracker - Money Out'].headers, filteredData['Money Tracker - Money Out'].rows, currentY);
      }

      // Save PDF
      const filterSuffix = Object.values(exportFilters).some(v => v) ? '_filtered' : '';
      doc.save(`Filtered_Export_${new Date().toISOString().split('T')[0]}${filterSuffix}.pdf`);

      setShowExportModal(false);
      resetExportFilters();
      alert('Export completed successfully!');
    } catch (error) {
      console.error('Error exporting filtered data:', error);
      alert(`Failed to export: ${error.message}`);
    } finally {
      setExporting(false);
    }
  };

  // Reset export filters
  const resetExportFilters = () => {
    setExportFilters({
      client: '',
      source: '',
      code: '',
      dateFrom: '',
      dateTo: '',
      sellingRate: '',
      buyingRate: '',
      currency: '',
      purpose: '',
      amountMin: '',
      amountMax: '',
      profitMin: '',
      profitMax: '',
      expenseMin: '',
      expenseMax: '',
      notes: '',
      name: ''
    });
  };

  const handleCancelExport = () => {
    setShowExportModal(false);
    setExportMonth('');
    setStockRate('');
    setExportType('monthly');
    resetExportFilters();
  };

  return (
    <>
      <aside className="sidebar">
        <div className="sidebar-header">
          <h1 className="sidebar-title">CX Excel</h1>
          <p className="sidebar-subtitle">Navigation</p>
          {user && (
            <div style={{ marginTop: '8px', fontSize: '0.75rem', color: '#6b7280' }}>
              Logged in as: <strong>{user.username}</strong>
            </div>
          )}
        </div>
        <nav className="sidebar-nav">
          <div className="create-buttons-container">
            <button
              onClick={handleCreateTable}
              className="create-btn create-btn-table"
              title="Create New Table"
            >
              + Table
            </button>
            <button
              onClick={() => setShowCreateNoteModal(true)}
              className="create-btn create-btn-note"
              title="Create New Note"
            >
              + Note
            </button>
            <button
              onClick={() => setShowExportModal(true)}
              className="create-btn create-btn-export"
              title="Export Tables to PDF"
            >
              + Export
            </button>
          </div>
          {notes.length > 0 && (
            <div className="sidebar-section">
              <div className="sidebar-section-title">Notes</div>
              {notes.map((note) => (
                <NavLink
                  key={note.note_id}
                  to={`/note/${note.note_id}`}
                  className={({ isActive }) =>
                    `sidebar-item sidebar-item-note ${isActive ? 'active' : ''}`
                  }
                >
                  <div className="sidebar-item-icon">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M14 2H6C5.46957 2 4.96086 2.21071 4.58579 2.58579C4.21071 2.96086 4 3.46957 4 4V20C4 20.5304 4.21071 21.0391 4.58579 21.4142C4.96086 21.7893 5.46957 22 6 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V8L14 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M14 2V8H20" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </div>
                  <span className="sidebar-item-text" title={note.note_name}>
                    {note.note_name || 'Untitled Note'}
                  </span>
                  <button
                    className="delete-note-btn"
                    onClick={(e) => handleDeleteNote(note.note_id, e)}
                    title="Delete note"
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M3 6H5H21M8 6V4C8 3.46957 8.21071 2.96086 8.58579 2.58579C8.96086 2.21071 9.46957 2 10 2H14C14.5304 2 15.0391 2.21071 15.4142 2.58579C15.7893 2.96086 16 3.46957 16 4V6M19 6V20C19 20.5304 18.7893 21.0391 18.4142 21.4142C18.0391 21.7893 17.5304 22 17 22H7C6.46957 22 5.96086 21.7893 5.58579 21.4142C5.21071 21.0391 5 20.5304 5 20V6H19Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M10 11V17M14 11V17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </button>
                </NavLink>
              ))}
            </div>
          )}
          <NavLink
            to="/dashboard"
            className={({ isActive }) =>
              `sidebar-item ${isActive ? 'active' : ''}`
            }
          >
            <div className="sidebar-item-icon">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M3 3H11V11H3V3Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M13 3H21V11H13V3Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M3 13H11V21H3V13Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M13 13H21V21H13V13Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <span className="sidebar-item-text">Dashboard</span>
          </NavLink>
          <NavLink
            to="/files"
            className={({ isActive }) =>
              `sidebar-item ${isActive ? 'active' : ''}`
            }
          >
            <div className="sidebar-item-icon">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M14 2H6C5.46957 2 4.96086 2.21071 4.58579 2.58579C4.21071 2.96086 4 3.46957 4 4V20C4 20.5304 4.21071 21.0391 4.58579 21.4142C4.96086 21.7893 5.46957 22 6 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V8L14 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M14 2V8H20" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M16 13H8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M16 17H8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M10 9H9H8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <span className="sidebar-item-text">File Manager</span>
          </NavLink>
          {tables.map((table, index) => (
            <div
              key={table.id}
              draggable
              onDragStart={(e) => handleDragStart(e, index)}
              onDragOver={(e) => handleDragOver(e, index)}
              onDragLeave={handleDragLeave}
              onDrop={(e) => handleDrop(e, index)}
              onDragEnd={handleDragEnd}
              className={`sidebar-draggable-item ${draggedIndex === index ? 'dragging' : ''} ${dragOverIndex === index ? 'drag-over' : ''}`}
            >
              <div className="drag-handle" title="Drag to reorder">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M9 5H11V7H9V5Z" fill="currentColor"/>
                  <path d="M9 11H11V13H9V11Z" fill="currentColor"/>
                  <path d="M9 17H11V19H9V17Z" fill="currentColor"/>
                  <path d="M13 5H15V7H13V5Z" fill="currentColor"/>
                  <path d="M13 11H15V13H13V11Z" fill="currentColor"/>
                  <path d="M13 17H15V19H13V17Z" fill="currentColor"/>
                </svg>
              </div>
              <NavLink
                to={table.path}
                className={({ isActive }) =>
                  `sidebar-item ${isActive ? 'active' : ''}`
                }
                onClick={(e) => {
                  // Allow navigation unless we're dragging
                  if (draggedIndex !== null) {
                    e.preventDefault();
                  }
                }}
              >
                <div className="sidebar-item-icon">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M3 3H21V21H3V3Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M3 9H21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M9 3V21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
                <span className="sidebar-item-text">{table.name}</span>
              </NavLink>
              {isCustomTable(table) && (
                <button
                  className="delete-table-btn"
                  onClick={(e) => handleDeleteTable(table.id || table.tableId, e)}
                  title="Delete table"
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M3 6H5H21M8 6V4C8 3.46957 8.21071 2.96086 8.58579 2.58579C8.96086 2.21071 9.46957 2 10 2H14C14.5304 2 15.0391 2.21071 15.4142 2.58579C15.7893 2.96086 16 3.46957 16 4V6M19 6V20C19 20.5304 18.7893 21.0391 18.4142 21.4142C18.0391 21.7893 17.5304 22 17 22H7C6.46957 22 5.96086 21.7893 5.58579 21.4142C5.21071 21.0391 5 20.5304 5 20V6H19Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M10 11V17M14 11V17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </button>
              )}
            </div>
          ))}
        </nav>
        <div className="sidebar-footer">
          <NavLink
            to="/settings"
            className={({ isActive }) =>
              `sidebar-item sidebar-item-footer ${isActive ? 'active' : ''}`
            }
          >
            <div className="sidebar-item-icon">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 15C13.6569 15 15 13.6569 15 12C15 10.3431 13.6569 9 12 9C10.3431 9 9 10.3431 9 12C9 13.6569 10.3431 15 12 15Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M19.4 15C19.2669 15.3016 19.2272 15.6362 19.286 15.9606C19.3448 16.285 19.4995 16.5843 19.73 16.82L19.79 16.88C19.976 17.0657 20.1235 17.2863 20.2241 17.5291C20.3248 17.7719 20.3766 18.0322 20.3766 18.295C20.3766 18.5578 20.3248 18.8181 20.2241 19.0609C20.1235 19.3037 19.976 19.5243 19.79 19.71C19.6043 19.896 19.3837 20.0435 19.1409 20.1441C18.8981 20.2448 18.6378 20.2966 18.375 20.2966C18.1122 20.2966 17.8519 20.2448 17.6091 20.1441C17.3663 20.0435 17.1457 19.896 16.96 19.71L16.9 19.65C16.6643 19.4195 16.365 19.2648 16.0406 19.206C15.7162 19.1472 15.3816 19.1869 15.08 19.32C14.7842 19.4468 14.532 19.6572 14.3543 19.9255C14.1766 20.1938 14.0813 20.5082 14.08 20.83V21C14.08 21.5304 13.8693 22.0391 13.4942 22.4142C13.1191 22.7893 12.6104 23 12.08 23C11.5496 23 11.0409 22.7893 10.6658 22.4142C10.2907 22.0391 10.08 21.5304 10.08 21V20.91C10.0723 20.579 9.96512 20.258 9.77251 19.9887C9.5799 19.7194 9.31074 19.5143 9 19.4C8.69838 19.2669 8.36381 19.2272 8.03941 19.286C7.71502 19.3448 7.41568 19.4995 7.18 19.73L7.12 19.79C6.93425 19.976 6.71368 20.1235 6.47088 20.2241C6.22808 20.3248 5.96783 20.3766 5.705 20.3766C5.44217 20.3766 5.18192 20.3248 4.93912 20.2241C4.69632 20.1235 4.47575 19.976 4.29 19.79C4.10405 19.6043 3.95653 19.3837 3.85588 19.1409C3.75523 18.8981 3.70343 18.6378 3.70343 18.375C3.70343 18.1122 3.75523 17.8519 3.85588 17.6091C3.95653 17.3663 4.10405 17.1457 4.29 16.96L4.35 16.9C4.58054 16.6643 4.73519 16.365 4.794 16.0406C4.85282 15.7162 4.81312 15.3816 4.68 15.08C4.55324 14.7842 4.34276 14.532 4.07447 14.3543C3.80618 14.1766 3.49179 14.0813 3.17 14.08H3C2.46957 14.08 1.96086 13.8693 1.58579 13.4942C1.21071 13.1191 1 12.6104 1 12.08C1 11.5496 1.21071 11.0409 1.58579 10.6658C1.96086 10.2907 2.46957 10.08 3 10.08H3.09C3.42099 10.0723 3.742 9.96512 4.01131 9.77251C4.28062 9.5799 4.48568 9.31074 4.6 9C4.73312 8.69838 4.77282 8.36381 4.714 8.03941C4.65519 7.71502 4.50054 7.41568 4.27 7.18L4.21 7.12C4.02405 6.93425 3.87653 6.71368 3.77588 6.47088C3.67523 6.22808 3.62343 5.96783 3.62343 5.705C3.62343 5.44217 3.67523 5.18192 3.77588 4.93912C3.87653 4.69632 4.02405 4.47575 4.21 4.29C4.39575 4.10405 4.61632 3.95653 4.85912 3.85588C5.10192 3.75523 5.36217 3.70343 5.625 3.70343C5.88783 3.70343 6.14808 3.75523 6.39088 3.85588C6.63368 3.95653 6.85425 4.10405 7.04 4.29L7.1 4.35C7.33568 4.58054 7.63502 4.73519 7.95941 4.794C8.28381 4.85282 8.61838 4.81312 8.92 4.68H9C9.29577 4.55324 9.54802 4.34276 9.72569 4.07447C9.90337 3.80618 9.99872 3.49179 10 3.17V3C10 2.46957 10.2107 1.96086 10.5858 1.58579C10.9609 1.21071 11.4696 1 12 1C12.5304 1 13.0391 1.21071 13.4142 1.58579C13.7893 1.96086 14 2.46957 14 3V3.09C14.0013 3.41179 14.0966 3.72618 14.2743 3.99447C14.452 4.26276 14.7042 4.47324 15 4.6C15.3016 4.73312 15.6362 4.77282 15.9606 4.714C16.285 4.65519 16.5843 4.50054 16.82 4.27L16.88 4.21C17.0657 4.02405 17.2863 3.87653 17.5291 3.77588C17.7719 3.67523 18.0322 3.62343 18.295 3.62343C18.5578 3.62343 18.8181 3.67523 19.0609 3.77588C19.3037 3.87653 19.5243 4.02405 19.71 4.21C19.896 4.39575 20.0435 4.61632 20.1441 4.85912C20.2448 5.10192 20.2966 5.36217 20.2966 5.625C20.2966 5.88783 20.2448 6.14808 20.1441 6.39088C20.0435 6.63368 19.896 6.85425 19.71 7.04L19.65 7.1C19.4195 7.33568 19.2648 7.63502 19.206 7.95941C19.1472 8.28381 19.1869 8.61838 19.32 8.92V9C19.4468 9.29577 19.6572 9.54802 19.9255 9.72569C20.1938 9.90337 20.5082 9.99872 20.83 10H21C21.5304 10 22.0391 10.2107 22.4142 10.5858C22.7893 10.9609 23 11.4696 23 12C23 12.5304 22.7893 13.0391 22.4142 13.4142C22.0391 13.7893 21.5304 14 21 14H20.91C20.5882 14.0013 20.2738 14.0966 20.0055 14.2743C19.7372 14.452 19.5268 14.7042 19.4 15Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <span className="sidebar-item-text">Settings</span>
          </NavLink>
        </div>
      </aside>

      {showCreateModal && (
        <div className="modal-overlay" onClick={handleCancel}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2 className="modal-title">Create New Table</h2>
            <div className="modal-form">
              <div className="form-group">
                <label htmlFor="table-name">Table Name:</label>
                <input
                  id="table-name"
                  type="text"
                  value={tableName}
                  onChange={(e) => setTableName(e.target.value)}
                  placeholder="Enter table name"
                  className="form-input"
                />
              </div>
              <div className="form-group">
                <label htmlFor="num-columns">Number of Columns:</label>
                <input
                  id="num-columns"
                  type="number"
                  min="1"
                  max="20"
                  value={numColumns}
                  onChange={handleNumColumnsChange}
                  className="form-input"
                />
              </div>
              <div className="form-group">
                <label>Column Names:</label>
                <div className="column-names-list">
                  {columnNames.map((name, index) => (
                    <input
                      key={index}
                      type="text"
                      value={name}
                      onChange={(e) => handleColumnNameChange(index, e.target.value)}
                      placeholder={`Column ${index + 1}`}
                      className="form-input column-input"
                    />
                  ))}
                </div>
              </div>
              <div className="modal-actions">
                <button onClick={handleCancel} className="btn btn-cancel">Cancel</button>
                <button onClick={handleCreateSubmit} className="btn btn-primary">Create Table</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showCreateNoteModal && (
        <div className="modal-overlay" onClick={handleCancelNote}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2 className="modal-title">Create New Note</h2>
            <div className="modal-form">
              <div className="form-group">
                <label htmlFor="note-name">Note Name:</label>
                <input
                  id="note-name"
                  type="text"
                  value={noteName}
                  onChange={(e) => setNoteName(e.target.value)}
                  placeholder="Enter note name"
                  className="form-input"
                  autoFocus
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleCreateNote();
                    } else if (e.key === 'Escape') {
                      handleCancelNote();
                    }
                  }}
                />
              </div>
              <div className="modal-actions">
                <button onClick={handleCancelNote} className="btn btn-cancel">Cancel</button>
                <button onClick={handleCreateNote} className="btn btn-primary">Create Note</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showExportModal && (
        <div className="modal-overlay" onClick={handleCancelExport}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '700px', maxHeight: '90vh', overflowY: 'auto' }}>
            <h2 className="modal-title">Export Data</h2>
            <div className="modal-form">
              {/* Export Type Selection - Tab Style */}
              <div className="form-group" style={{ marginBottom: '24px' }}>
                <div style={{ 
                  display: 'flex', 
                  gap: '0', 
                  borderBottom: '2px solid #e5e7eb',
                  marginBottom: '20px'
                }}>
                  <button
                    type="button"
                    onClick={() => setExportType('monthly')}
                    style={{
                      flex: 1,
                      padding: '12px 16px',
                      border: 'none',
                      background: exportType === 'monthly' ? '#ffffff' : 'transparent',
                      borderBottom: exportType === 'monthly' ? '3px solid #3b82f6' : '3px solid transparent',
                      color: exportType === 'monthly' ? '#3b82f6' : '#6b7280',
                      fontWeight: exportType === 'monthly' ? '600' : '400',
                      cursor: 'pointer',
                      fontSize: '14px',
                      transition: 'all 0.2s',
                      borderRadius: '8px 8px 0 0'
                    }}
                  >
                    Monthly Export
                  </button>
                  <button
                    type="button"
                    onClick={() => setExportType('stockRate')}
                    style={{
                      flex: 1,
                      padding: '12px 16px',
                      border: 'none',
                      background: exportType === 'stockRate' ? '#ffffff' : 'transparent',
                      borderBottom: exportType === 'stockRate' ? '3px solid #3b82f6' : '3px solid transparent',
                      color: exportType === 'stockRate' ? '#3b82f6' : '#6b7280',
                      fontWeight: exportType === 'stockRate' ? '600' : '400',
                      cursor: 'pointer',
                      fontSize: '14px',
                      transition: 'all 0.2s',
                      borderRadius: '8px 8px 0 0'
                    }}
                  >
                    Stock Rate History
                  </button>
                  <button
                    type="button"
                    onClick={() => setExportType('filtered')}
                    style={{
                      flex: 1,
                      padding: '12px 16px',
                      border: 'none',
                      background: exportType === 'filtered' ? '#ffffff' : 'transparent',
                      borderBottom: exportType === 'filtered' ? '3px solid #3b82f6' : '3px solid transparent',
                      color: exportType === 'filtered' ? '#3b82f6' : '#6b7280',
                      fontWeight: exportType === 'filtered' ? '600' : '400',
                      cursor: 'pointer',
                      fontSize: '14px',
                      transition: 'all 0.2s',
                      borderRadius: '8px 8px 0 0'
                    }}
                  >
                    Filtered Export
                  </button>
                </div>
              </div>

              {/* Monthly Export Options */}
              {exportType === 'monthly' && (
                <div style={{ padding: '20px 0' }}>
              <div className="form-group">
                <label htmlFor="export-month">Month:</label>
                <select
                  id="export-month"
                  value={exportMonth}
                  onChange={(e) => setExportMonth(e.target.value)}
                  className="form-input"
                >
                  <option value="">Select Month</option>
                  <option value="1">January</option>
                  <option value="2">February</option>
                  <option value="3">March</option>
                  <option value="4">April</option>
                  <option value="5">May</option>
                  <option value="6">June</option>
                  <option value="7">July</option>
                  <option value="8">August</option>
                  <option value="9">September</option>
                  <option value="10">October</option>
                  <option value="11">November</option>
                  <option value="12">December</option>
                </select>
              </div>
              <div className="form-group">
                <label htmlFor="export-year">Year:</label>
                <input
                  id="export-year"
                  type="number"
                  value={exportYear}
                  onChange={(e) => setExportYear(e.target.value)}
                  placeholder="Year"
                  className="form-input"
                  min="2000"
                  max="2100"
                />
              </div>
                  <p style={{ fontSize: '0.875rem', color: '#6b7280', marginTop: '16px', lineHeight: '1.5' }}>
                    Export all tables data for the selected month and year.
                  </p>
                </div>
              )}

              {/* Stock Rate History Export */}
              {exportType === 'stockRate' && (
                <div style={{ padding: '20px 0' }}>
                <div className="form-group">
                  <label htmlFor="stock-rate">Stock Rate (Buying Rate):</label>
                  <input
                    id="stock-rate"
                    type="number"
                    step="0.01"
                    value={stockRate}
                    onChange={(e) => setStockRate(e.target.value)}
                    placeholder="e.g., 22.10"
                    className="form-input"
                    autoFocus
                  />
                  </div>
                  <div style={{ 
                    marginTop: '16px', 
                    padding: '12px', 
                    backgroundColor: '#f9fafb', 
                    borderRadius: '6px',
                    border: '1px solid #e5e7eb'
                  }}>
                    <p style={{ fontSize: '0.875rem', color: '#374151', margin: 0, lineHeight: '1.6' }}>
                      <strong>What will be exported:</strong>
                    </p>
                    <ul style={{ fontSize: '0.875rem', color: '#6b7280', margin: '8px 0 0 0', paddingLeft: '20px', lineHeight: '1.6' }}>
                      <li>Stock Updates (when stock was added/updated)</li>
                      <li>Transactions (when transactions were done)</li>
                      <li>Current Balance (current stock balance)</li>
                      <li>Source, Client, Date, and all related information</li>
                    </ul>
                  </div>
                </div>
              )}

              {/* Filtered Export Options */}
              {exportType === 'filtered' && (
                <div style={{ 
                  maxHeight: '60vh', 
                  overflowY: 'auto', 
                  padding: '20px', 
                  backgroundColor: '#f9fafb',
                  borderRadius: '8px',
                  border: '1px solid #e5e7eb'
                }}>
                  <p style={{ 
                    fontSize: '0.875rem', 
                    color: '#6b7280', 
                    marginBottom: '20px',
                    lineHeight: '1.5'
                  }}>
                    Apply filters to export specific data. Leave fields empty to include all records.
                  </p>
                  
                  <div style={{ 
                    display: 'grid', 
                    gridTemplateColumns: 'repeat(2, 1fr)', 
                    gap: '16px',
                    marginBottom: '20px'
                  }}>
                  
                    {/* Client/Name Filter */}
                    <div className="form-group" style={{ marginBottom: 0 }}>
                      <label style={{ display: 'block', marginBottom: '6px', fontSize: '0.875rem', fontWeight: '500', color: '#374151' }}>Client/Name:</label>
                      <input
                        type="text"
                        value={exportFilters.client || exportFilters.name}
                        onChange={(e) => {
                          const value = e.target.value;
                          setExportFilters(prev => ({ ...prev, client: value, name: value }));
                        }}
                        placeholder="Client or name"
                        className="form-input"
                      />
                    </div>

                    {/* Source Filter */}
                    <div className="form-group" style={{ marginBottom: 0 }}>
                      <label style={{ display: 'block', marginBottom: '6px', fontSize: '0.875rem', fontWeight: '500', color: '#374151' }}>Source:</label>
                      <input
                        type="text"
                        value={exportFilters.source}
                        onChange={(e) => setExportFilters(prev => ({ ...prev, source: e.target.value }))}
                        placeholder="Source name"
                        className="form-input"
                      />
                    </div>

                    {/* Code Filter */}
                    <div className="form-group" style={{ marginBottom: 0 }}>
                      <label style={{ display: 'block', marginBottom: '6px', fontSize: '0.875rem', fontWeight: '500', color: '#374151' }}>Code:</label>
                      <input
                        type="text"
                        value={exportFilters.code}
                        onChange={(e) => setExportFilters(prev => ({ ...prev, code: e.target.value }))}
                        placeholder="Code"
                        className="form-input"
                      />
                    </div>

                    {/* Purpose Filter */}
                    <div className="form-group" style={{ marginBottom: 0 }}>
                      <label style={{ display: 'block', marginBottom: '6px', fontSize: '0.875rem', fontWeight: '500', color: '#374151' }}>Purpose:</label>
                      <input
                        type="text"
                        value={exportFilters.purpose}
                        onChange={(e) => setExportFilters(prev => ({ ...prev, purpose: e.target.value }))}
                        placeholder="Purpose"
                        className="form-input"
                      />
                  </div>

                    {/* Date From */}
                    <div className="form-group" style={{ marginBottom: 0 }}>
                      <label style={{ display: 'block', marginBottom: '6px', fontSize: '0.875rem', fontWeight: '500', color: '#374151' }}>Date From:</label>
                      <input
                        type="text"
                        value={exportFilters.dateFrom}
                        onChange={(e) => setExportFilters(prev => ({ ...prev, dateFrom: e.target.value }))}
                        placeholder="dd/mm/yyyy"
                        className="form-input"
                      />
                    </div>

                    {/* Date To */}
                    <div className="form-group" style={{ marginBottom: 0 }}>
                      <label style={{ display: 'block', marginBottom: '6px', fontSize: '0.875rem', fontWeight: '500', color: '#374151' }}>Date To:</label>
                      <input
                        type="text"
                        value={exportFilters.dateTo}
                        onChange={(e) => setExportFilters(prev => ({ ...prev, dateTo: e.target.value }))}
                        placeholder="dd/mm/yyyy"
                        className="form-input"
                      />
                    </div>

                    {/* Currency Filter */}
                    <div className="form-group" style={{ marginBottom: 0 }}>
                      <label style={{ display: 'block', marginBottom: '6px', fontSize: '0.875rem', fontWeight: '500', color: '#374151' }}>Currency:</label>
                      <select
                        value={exportFilters.currency}
                        onChange={(e) => setExportFilters(prev => ({ ...prev, currency: e.target.value }))}
                        className="form-input"
                      >
                        <option value="">All Currencies</option>
                        <option value="INR">INR</option>
                        <option value="AED">AED</option>
                      </select>
                  </div>

                    {/* Notes Filter */}
                    <div className="form-group" style={{ marginBottom: 0 }}>
                      <label style={{ display: 'block', marginBottom: '6px', fontSize: '0.875rem', fontWeight: '500', color: '#374151' }}>Notes:</label>
                      <input
                        type="text"
                        value={exportFilters.notes}
                        onChange={(e) => setExportFilters(prev => ({ ...prev, notes: e.target.value }))}
                        placeholder="Search in notes"
                        className="form-input"
                      />
                    </div>
                  </div>

                  {/* Advanced Filters Section */}
                  <details style={{ marginTop: '20px', borderTop: '1px solid #e5e7eb', paddingTop: '20px' }}>
                    <summary style={{ 
                      cursor: 'pointer', 
                      fontSize: '0.875rem', 
                      fontWeight: '500', 
                      color: '#374151',
                      marginBottom: '16px',
                      userSelect: 'none'
                    }}>
                      Advanced Filters
                    </summary>
                    
                    <div style={{ 
                      display: 'grid', 
                      gridTemplateColumns: 'repeat(2, 1fr)', 
                      gap: '16px',
                      marginTop: '16px'
                    }}>
                      {/* Selling Rate */}
                      <div className="form-group" style={{ marginBottom: 0 }}>
                        <label style={{ display: 'block', marginBottom: '6px', fontSize: '0.875rem', fontWeight: '500', color: '#374151' }}>Selling Rate:</label>
                      <input
                        type="number"
                        step="0.01"
                        value={exportFilters.sellingRate}
                        onChange={(e) => setExportFilters(prev => ({ ...prev, sellingRate: e.target.value }))}
                          placeholder="Exact rate"
                        className="form-input"
                      />
                    </div>

                      {/* Buying Rate */}
                      <div className="form-group" style={{ marginBottom: 0 }}>
                        <label style={{ display: 'block', marginBottom: '6px', fontSize: '0.875rem', fontWeight: '500', color: '#374151' }}>Buying Rate:</label>
                      <input
                        type="number"
                        step="0.01"
                        value={exportFilters.buyingRate}
                        onChange={(e) => setExportFilters(prev => ({ ...prev, buyingRate: e.target.value }))}
                          placeholder="Exact rate"
                        className="form-input"
                      />
                  </div>

                      {/* Amount Min */}
                      <div className="form-group" style={{ marginBottom: 0 }}>
                        <label style={{ display: 'block', marginBottom: '6px', fontSize: '0.875rem', fontWeight: '500', color: '#374151' }}>Amount Min:</label>
                      <input
                        type="number"
                        step="0.01"
                        value={exportFilters.amountMin}
                        onChange={(e) => setExportFilters(prev => ({ ...prev, amountMin: e.target.value }))}
                          placeholder="Minimum"
                        className="form-input"
                      />
                    </div>

                      {/* Amount Max */}
                      <div className="form-group" style={{ marginBottom: 0 }}>
                        <label style={{ display: 'block', marginBottom: '6px', fontSize: '0.875rem', fontWeight: '500', color: '#374151' }}>Amount Max:</label>
                      <input
                        type="number"
                        step="0.01"
                        value={exportFilters.amountMax}
                        onChange={(e) => setExportFilters(prev => ({ ...prev, amountMax: e.target.value }))}
                          placeholder="Maximum"
                        className="form-input"
                      />
                  </div>

                      {/* Profit Min */}
                      <div className="form-group" style={{ marginBottom: 0 }}>
                        <label style={{ display: 'block', marginBottom: '6px', fontSize: '0.875rem', fontWeight: '500', color: '#374151' }}>Profit Min:</label>
                      <input
                        type="number"
                        step="0.01"
                        value={exportFilters.profitMin}
                        onChange={(e) => setExportFilters(prev => ({ ...prev, profitMin: e.target.value }))}
                          placeholder="Minimum"
                        className="form-input"
                      />
                    </div>

                      {/* Profit Max */}
                      <div className="form-group" style={{ marginBottom: 0 }}>
                        <label style={{ display: 'block', marginBottom: '6px', fontSize: '0.875rem', fontWeight: '500', color: '#374151' }}>Profit Max:</label>
                      <input
                        type="number"
                        step="0.01"
                        value={exportFilters.profitMax}
                        onChange={(e) => setExportFilters(prev => ({ ...prev, profitMax: e.target.value }))}
                          placeholder="Maximum"
                        className="form-input"
                      />
                  </div>

                      {/* Expense Min */}
                      <div className="form-group" style={{ marginBottom: 0 }}>
                        <label style={{ display: 'block', marginBottom: '6px', fontSize: '0.875rem', fontWeight: '500', color: '#374151' }}>Expense Min:</label>
                      <input
                        type="number"
                        step="0.01"
                        value={exportFilters.expenseMin}
                        onChange={(e) => setExportFilters(prev => ({ ...prev, expenseMin: e.target.value }))}
                          placeholder="Minimum"
                        className="form-input"
                      />
                    </div>

                      {/* Expense Max */}
                      <div className="form-group" style={{ marginBottom: 0 }}>
                        <label style={{ display: 'block', marginBottom: '6px', fontSize: '0.875rem', fontWeight: '500', color: '#374151' }}>Expense Max:</label>
                      <input
                        type="number"
                        step="0.01"
                        value={exportFilters.expenseMax}
                        onChange={(e) => setExportFilters(prev => ({ ...prev, expenseMax: e.target.value }))}
                          placeholder="Maximum"
                        className="form-input"
                      />
                    </div>
                  </div>
                  </details>

                  <div style={{ marginTop: '20px', display: 'flex', justifyContent: 'flex-end' }}>
                  <button
                    onClick={resetExportFilters}
                    className="btn btn-cancel"
                      style={{ fontSize: '0.875rem', padding: '8px 16px' }}
                  >
                      Reset All Filters
                  </button>
                  </div>
                </div>
              )}

              <div className="modal-actions">
                <button onClick={handleCancelExport} className="btn btn-cancel" disabled={exporting}>
                  Cancel
                </button>
                <button 
                  onClick={handleExport} 
                  className="btn btn-primary" 
                  disabled={exporting}
                >
                  {exporting ? 'Exporting...' : 'Export PDF'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Sidebar;

